import React from 'react';
import {
  Brain, TrendingUp, Target, BarChart3, Zap,
  Activity, AlertTriangle, Clock, Wrench, Stethoscope,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const WindTurbinesAIPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'AI-Driven Analytics',
      title: (
        <>
          Apply pre-trained models <em>to wind turbine performance</em>
        </>
      ),
      lede: 'Apply pre-trained models to identify root causes of energy losses and bridge the gap between target and actual generation with our advanced AI-driven analytics platform.',
      image: '/assets/img/general/windmills-1838788_1280.jpg',
      imageAlt: 'Wind turbines analysed by AI models',
      actions: [
        { label: 'Talk to us', to: '/contact' },
        { label: 'Explore all services', to: '/service' },
      ],
      metrics: [
        { value: '25%', label: 'Energy increase' },
        { value: '35%', label: 'Cost reduction' },
        { value: '99.5%', label: 'Accuracy rate' },
        { value: '24/7', label: 'Monitoring' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'AI-powered analytics solutions',
      lede: 'Leverage cutting-edge artificial intelligence to optimize wind energy operations.',
      checklist: [
        'Increase energy generation by 15-25%',
        'Reduce maintenance costs by 35%',
        'Improve turbine efficiency by 20%',
        'Minimize downtime through predictive maintenance',
      ],
      image: '/assets/img/general/pinwheels-6535595_1280.jpg',
      imageAlt: 'Wind farm generating analysed telemetry',
      imageCaption: 'Models trained specifically on wind asset behaviour',
    }}
    capabilities={{
      eyebrow: 'Key benefits',
      title: 'Transform your wind energy operations',
      lede: 'AI-driven analytics applied across the wind portfolio.',
      items: [
        {
          icon: Brain,
          title: 'AI-powered analysis',
          description: 'Advanced machine learning models analyze wind patterns and turbine performance data.',
        },
        {
          icon: Target,
          title: 'Root cause identification',
          description: 'Intelligent algorithms identify the root causes of energy losses and inefficiencies.',
        },
        {
          icon: TrendingUp,
          title: 'Performance optimization',
          description: 'AI-driven recommendations to bridge the gap between target and actual generation.',
        },
        {
          icon: BarChart3,
          title: 'Predictive analytics',
          description: 'Forecast potential issues and optimize energy output using historical data.',
        },
      ],
    }}
    coverage={{
      eyebrow: 'AI capabilities',
      title: 'Purpose-built models and engines',
      lede: 'Engineered to keep turbines at peak output.',
      items: [
        {
          icon: Brain,
          title: 'Machine learning models',
          description: 'Pre-trained models specifically designed for wind energy optimization.',
        },
        {
          icon: Zap,
          title: 'Real-time processing',
          description: 'Instant analysis of turbine data for immediate optimization.',
        },
        {
          icon: Activity,
          title: 'Pattern recognition',
          description: 'Identify complex patterns in wind behavior and turbine performance.',
        },
        {
          icon: AlertTriangle,
          title: 'Anomaly detection',
          description: 'Early detection of potential issues before they impact performance.',
        },
      ],
    }}
    outcomes={{
      eyebrow: 'Real-world outcomes',
      title: 'See how AI-driven analytics deliver measurable results',
      items: [
        {
          icon: Clock,
          title: 'Reduced downtime',
          description: 'Instant alerts enable faster response and minimize generation losses.',
        },
        {
          icon: Wrench,
          title: 'Improved turbine longevity',
          description: 'Catch anomalies early and schedule predictive maintenance.',
        },
        {
          icon: Stethoscope,
          title: 'Actionable diagnostics',
          description: 'Automated root-cause analysis guides technicians to quick, effective solutions.',
        },
      ],
    }}
    cta={{
      title: 'Ready to put AI on your turbines?',
      body: 'Request a walkthrough of our AI-driven wind analytics platform.',
      primary: { label: 'Talk to us', to: '/contact' },
      secondary: { label: 'View all services', to: '/service' },
    }}
  />
);

export default WindTurbinesAIPage;
